import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { CenterModal } from "../CenterModal";
import { useToast } from "../ToastProvider";
import type { InAppPlatform } from "~/utils/authTelemetry";

interface InAppBrowserNoticeProps {
  open: boolean;
  onClose: () => void;
  platform: InAppPlatform;
}

export function InAppBrowserNotice({
  open,
  onClose,
  platform,
}: InAppBrowserNoticeProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showActionToast } = useToast();

  const handleOpenInChrome = () => {
    // Android intent scheme — Chrome consumes the URL after `intent://`,
    // preserving the path/query of the current page.
    const { host, pathname, search, hash } = window.location;
    const suffix = `${pathname}${search}${hash}`;
    window.location.href = `intent://${host}${suffix}#Intent;scheme=https;package=com.android.chrome;end`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showActionToast({ title: t("auth.login.inAppBrowser.copyLinkDone"), severity: "success" });
    } catch {
      // Some in-app browsers block clipboard access.
    }
  };

  return (
    <CenterModal
      open={open}
      onClose={onClose}
      title={t("auth.login.inAppBrowser.title")}
      size="sm"
    >
      <Stack spacing={2.5} sx={{ pb: 3, pt: 0.5, alignItems: "center" }}>
        <Box
          aria-hidden
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: theme.palette.custom.primary,
          }}
        >
          <OpenInNewRoundedIcon sx={{ fontSize: 40 }} />
        </Box>

        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.6,
            color: theme.palette.custom.gray[700],
            textAlign: "center",
          }}
        >
          {t("auth.login.inAppBrowser.description")}
        </Typography>

        <Stack spacing={1.5} sx={{ width: "100%" }}>
          {platform === "android" && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleOpenInChrome}
              sx={{ minHeight: 52, borderRadius: "8px" }}
            >
              {t("auth.login.inAppBrowser.openInChrome")}
            </Button>
          )}

          {platform === "ios" && (
            <Box
              sx={{
                px: 2,
                py: 1.75,
                borderRadius: "8px",
                backgroundColor: theme.palette.custom.gray[100],
                border: `1px solid ${theme.palette.custom.gray[200]}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: theme.palette.custom.gray[700],
                }}
              >
                {t("auth.login.inAppBrowser.iosInstructions")}
              </Typography>
            </Box>
          )}

          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={handleCopyLink}
            sx={{ minHeight: 52, borderRadius: "8px" }}
          >
            {t("auth.login.inAppBrowser.copyLink")}
          </Button>
        </Stack>
      </Stack>
    </CenterModal>
  );
}
